import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Link from '../components/SafeLink';
import { Header, Footer } from '../components/Layout';
import { Icon, T, useText } from '../components/Text';
import { usePage } from '../hooks/useResource';
import { getSettings, defaultSettings } from '../firebase/homeApi';
import { landingContent, phoneHref } from './landingContent';
import '../assets/theme/landing.css';

const serviceIcons = {
  'Değerli Maden Dökümü': 'services',
  '3D Mum Basımı': 'cube',
  'Kalıp Kesimi': 'icons8-scissors-96',
};

function Lines({ text, accent = false }) {
  return String(text || '').split('\n').map((line, index, lines) => <React.Fragment key={index}>
    {index > 0 && <br />}{accent && index === lines.length - 1 ? <em><T>{line}</T></em> : <T>{line}</T>}
  </React.Fragment>);
}

/**
 * Zirve Döküm Landing / Vitrin Sayfası (/)
 * Firestore (settings/home) üzerinden dinamik içerik çeker, lüks tasarım dilini korur.
 */
export default function Landing() {
  const { t } = useText();
  const route = useLocation();
  const [settings, setSettings] = useState(defaultSettings);

  usePage('Zirve Döküm', '');

  // Firestore üzerinden canlı içerik dinleme
  useEffect(() => {
    const unsubscribe = getSettings((data) => {
      if (data) setSettings(data);
    });
    return () => unsubscribe();
  }, []);

  // Tawk.to Canlı Destek Entegrasyonu (Eski sistemden korundu)
  useEffect(() => {
    if (document.getElementById('tawk-script')) return;
    const s1 = document.createElement('script');
    s1.id = 'tawk-script';
    s1.async = true;
    s1.src = 'https://embed.tawk.to/63fc89994247f20fefe2df18/1gq97j02s';
    s1.charset = 'UTF-8';
    s1.setAttribute('crossorigin', '*');
    document.body.appendChild(s1);
  }, []);

  // Hash link kaydırma (#hizmetler, #iletisim vb.)
  useEffect(() => {
    if (route.hash) {
      document.querySelector(route.hash)?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [route.hash]);

  const content = landingContent(settings);
  // Keep the local showcase's layout/copy; read original factual content separately.
  const hero = { ...defaultSettings.hero, image: content.hero.image };
  const services = {
    ...defaultSettings.services,
    items: content.services.items.map(item => {
      const local = defaultSettings.services.items.find(service => service.title === item.title) || {};
      return { ...local, ...item, code: item.code || local.code, description: item.description || local.description, cta: item.cta || local.cta };
    }),
  };
  const about = { ...content.about, title: defaultSettings.about.title };
  const contact = { ...content.contact, branches: content.contact.branches.map(branch => {
    const name = branch.name.toLocaleLowerCase('tr-TR');
    const local = name.includes('çarşı') ? defaultSettings.contact.branches[0] : name.includes('kent') ? defaultSettings.contact.branches[1] : null;
    return { ...branch, heading: local?.heading || branch.heading };
  }) };
  const { instagram } = content;

  return (
    <>
      <Header publicPage />
      <main id="main" className="landing-page">
        {/* 1. Hero Bölümü */}
        <section className="hero" aria-labelledby="hero-title">
          <div className="hero-copy">
            <div className="eyebrow">
              <span />
              <T>{hero.eyebrow || 'ZİRVE DÖKÜM · İSTANBUL'}</T>
            </div>
            <h1 id="hero-title" style={{ whiteSpace: 'pre-line' }}>
              <Lines text={hero.title} accent />
            </h1>
            <p style={{ whiteSpace: 'pre-line' }}>
              <Lines text={hero.subtitle} />
            </p>
            <div className="hero-actions">
              <Link className="button gold" to="/orders">
                <T>Siparişlerimi Gör</T>
                <Icon name="list-view" />
              </Link>
              <a className="text-link" href="#hizmetler">
                <T>Hizmetleri keşfet </T>
                <Icon name="right" className="rotate-down" />
              </a>
            </div>
            <div className="hero-note">
              <span className="note-symbol" aria-hidden="true">
                <Icon name="star" />
              </span>
              <span style={{ whiteSpace: 'pre-line' }}>
                {hero.note.split('\n').map((line, index) => <React.Fragment key={index}>
                  {index > 0 && <br />}{index > 0 ? <strong><T>{line}</T></strong> : <T>{line}</T>}
                </React.Fragment>)}
              </span>
            </div>
          </div>

          <div className="hero-visual">
            <img
              src={hero.image}
              alt={t('Atölyede değerli maden döküm işlemi')}
              fetchPriority="high"
            />
            <div className="visual-shade" />
            <div className="visual-label">
              <span>
                <T>ATEŞTEN FORMA.</T>
              </span>
              <p>
                <T>Her parçada</T>
                <br />
                <T>ustalığın izi.</T>
              </p>
            </div>
            <div className="image-caption">
              <span>
                <T>HASSASİYET. EMEK. ZİRVE.</T>
              </span>
              <span>
                <T>01 / DÖKÜM</T>
              </span>
            </div>
          </div>
        </section>

        {/* 2. Değerler / Özellikler Şeridi */}
        <div className="values" aria-label={t('Çalışma anlayışımız')}>
          {(hero.features || defaultSettings.hero.features).map((feat, idx) => (
            <span key={idx}>
              <i>{String(idx + 1).padStart(2, '0')}</i>
              <T>{` ${feat}`}</T>
            </span>
          ))}
          <a href="#iletisim">
            <T>İstanbul’da iki şube </T>
            <Icon name="shop" />
          </a>
        </div>

        {/* 3. Hizmetler Bölümü */}
        <section id="hizmetler" className="services section">
          <div className="section-heading">
            <div>
              <div className="eyebrow">
                <T>{services.eyebrow || 'UZMANLIĞIMIZ'}</T>
              </div>
              <h2 style={{ whiteSpace: 'pre-line' }}>
                <Lines text={services.title} />
              </h2>
            </div>
            <p style={{ whiteSpace: 'pre-line' }}>
              <Lines text={services.subtitle} />
            </p>
          </div>

          <div className="service-grid">
            {(services.items || defaultSettings.services.items).map((item, idx) => (
              <a key={idx} className="service-card" href="#iletisim">
                <div className="card-top">
                  <span>{item.code || `0${idx + 1}`}</span>
                  <Icon name={serviceIcons[item.title]} className="card-arrow" />
                </div>
                <div className={`service-art ${item.image ? `service-photo ${item.art || ''}` : 'service-icon-art'}`}>
                  {item.image ? <img
                    src={item.image}
                    alt={item.title}
                    loading="lazy"
                    width="600"
                    height="519"
                  /> : <Icon name={item.visualIcon} />}
                </div>
                <h3>
                  <T>{item.title}</T>
                </h3>
                {item.description && <p>
                  <T>{item.description}</T>
                </p>}
                <span className="card-bottom">
                  <T>{item.cta || 'Detaylı bilgi alın'}</T>
                  <Icon name="right" />
                </span>
              </a>
            ))}
          </div>
        </section>

        {/* 4. Müşteri Takip Paneli Tanıtımı */}
        <section className="tracking section">
          <div>
            <div className="eyebrow">
              <T>MÜŞTERİLERİMİZE ÖZEL</T>
            </div>
            <h2>
              <T>Üretim bizde.</T>
              <br />
              <em>
                <T>Takibi sizde.</T>
              </em>
            </h2>
            <p>
              <T>
                Yüklediğimiz ürünleri ve siparişlerinizi müşteri panelinizden inceleyin. İşinizin
                takibi, elinizin altında olsun.
              </T>
            </p>
            <Link className="button gold" to="/orders">
              <T>Müşteri Paneline Git</T>
              <Icon name="customers" />
            </Link>
          </div>

          <div className="tracking-graphic" aria-hidden="true">
            <div className="orbit" />
            <img src="/assets/logo.png" alt="Zirve Döküm Logo" />
            <span className="graphic-tag first">
              <T>ÜRÜNLERİNİZ</T>
            </span>
            <span className="graphic-tag second">
              <T>SİPARİŞLERİNİZ</T>
            </span>
            <span className="graphic-tag third">
              <T>TEK BİR YERDE</T>
            </span>
          </div>
        </section>

        {/* 5. Hakkımızda */}
        <section id="hakkimizda" className="about section">
          <div className="about-image">
            <img
              src={about.image || '/assets/atelier.jpg'}
              alt={t('Zirve Döküm iş yerinden bir görünüm')}
              loading="lazy"
            />
            <span>ZİRVE DÖKÜM / İSTANBUL</span>
          </div>

          <div className="about-copy">
            <div className="eyebrow">
              <T>{about.eyebrow || 'BİZİ TANIYIN'}</T>
            </div>
            <h2 style={{ whiteSpace: 'pre-line' }}>
              <Lines text={about.title} accent />
            </h2>
            <p>
              <T>
                {about.description1 ||
                  'Her tasarımın bir emeği, her parçanın bir hikâyesi var. Zirve Döküm olarak, o emeği üretimin her aşamasında aynı titizlikle ele alıyoruz.'}
              </T>
            </p>
            <p>
              <T>
                {about.description2 ||
                  'Değerli maden dökümü, mum basımı ve kalıp kesimi hizmetlerimizle kuyumculuk üretiminize eşlik ediyoruz. Çarşı ve Kuyumcukent şubelerimizde sizinle tanışmayı bekliyoruz.'}
              </T>
            </p>
            <a className="text-link" href="#iletisim">
              <T>Birlikte üretelim </T>
              <Icon name="services" />
            </a>
          </div>
        </section>

        {/* 6. İletişim ve Şubeler */}
        <section id="iletisim" className="contact section">
          <div className="section-heading">
            <div>
              <div className="eyebrow">
                <T>{contact.eyebrow || 'BİRLİKTE ÜRETELİM'}</T>
              </div>
              <h2 style={{ whiteSpace: 'pre-line' }}>
                <Lines text={contact.title || 'Bir sonraki tasarımınız\nburada hayat bulsun.'} />
              </h2>
            </div>
            {contact.callPhone && <a className="button dark" href={phoneHref(contact.callPhone)}>
              <T>Bizi Arayın </T>
              <Icon name="phone" />
            </a>}
          </div>

          <div className="contact-grid">
            {/* Şubeler (Çarşı & Kuyumcukent) */}
            {(contact.branches || defaultSettings.contact.branches).map((branch, idx) => (
              <article key={idx}>
                <span className="branch-label">{branch.name}</span>
                <h3>
                  <T>{branch.heading}</T>
                </h3>
                <p style={{ whiteSpace: 'pre-line' }}>
                  <T>{branch.address}</T>
                </p>
                {branch.directionsUrl && (
                  <a className="has-ui-icon" href={branch.directionsUrl} target="_blank" rel="noopener noreferrer">
                    <T>Yol tarifi al</T><Icon name="location" />
                  </a>
                )}
              </article>
            ))}

            {/* Doğrudan İletişim */}
            <article className="contact-direct">
              <span className="branch-label">İLETİŞİM</span>
              {contact.phones.map((phone, index) => <a key={index} className="phone" href={phoneHref(phone.value)}>
                <Icon name="phone" className="leading-icon" />
                <span className="phone-branch"><T>{phone.title}</T></span> {phone.value}
              </a>)}
              {contact.emails.map((email, index) => <a key={index} href={`mailto:${email.value}`}>{email.value} <Icon name="at" /></a>)}
              {contact.workingHours && (
                <p style={{ whiteSpace: 'pre-line' }}>
                  <Lines text={contact.workingHours} />
                </p>
              )}
            </article>
          </div>
        </section>
      </main>
      <Footer publicPage instagramUrl={instagram} />
    </>
  );
}
